import { z } from 'zod';
import { flushLocalDb, getMeta, setMeta } from '../db/local.js';
import {
  SharedTabletOperatorSchema,
  type SharedTabletOperator,
  type SharedTabletUnlockSuccess,
} from './sharedTabletOperatorTypes.js';

const LOCAL_PIN_ITERATIONS = 120_000;
const OFFLINE_GRANT_MAX_AGE_MS = 24 * 60 * 60_000;
const CACHE_VERSION = 3;

const CachedOperatorCredentialSchema = z.object({
  tenant_personnel_id: z.string().uuid(),
  full_name: z.string().trim().min(1).max(200),
  role: z.string().trim().min(1).max(100),
  app_role: z.enum(['consulente', 'ristoratore', 'osa', 'staff']),
  account_user_id: z.string().uuid().nullable(),
  identity_fingerprint: z.string().regex(/^[0-9a-f]{64}$/),
  // Legacy cache entries do not prove that the server authorized no-PIN access.
  pin_required: z.boolean().default(true),
  pin_version: z.number().int().positive(),
  grant_id: z.string().uuid(),
  grant_token_ciphertext: z.string().regex(/^[0-9a-f]+$/),
  grant_token_salt: z.string().regex(/^[0-9a-f]{32}$/),
  grant_token_iv: z.string().regex(/^[0-9a-f]{24}$/),
  grant_expires_at: z.string().datetime({ offset: true }),
  offline_expires_at: z.string().datetime({ offset: true }),
  verifier_salt: z.string().regex(/^[0-9a-f]{32}$/),
  verifier_hash: z.string().regex(/^[0-9a-f]{64}$/),
  failed_attempts: z.number().int().nonnegative().default(0),
  locked_until: z.string().datetime({ offset: true }).nullable().default(null),
});
export type CachedOperatorCredential = z.infer<typeof CachedOperatorCredentialSchema>;

const OperatorCredentialCacheSchema = z.object({
  version: z.literal(CACHE_VERSION),
  operators: z.array(SharedTabletOperatorSchema).max(1_000),
  credentials: z.record(CachedOperatorCredentialSchema),
  updated_at: z.string().datetime({ offset: true }),
});
type OperatorCredentialCache = z.infer<typeof OperatorCredentialCacheSchema>;

export interface OperatorCacheScope {
  tenantId: string;
  tabletUserId: string;
  deviceId: string;
}

export interface ActiveSharedTabletOperator {
  tenantPersonnelId: string;
  fullName: string;
  role: string;
  appRole: 'consulente' | 'ristoratore' | 'osa' | 'staff';
  accountUserId: string | null;
  identityFingerprint: string;
  pinVersion: number;
  grantId: string;
  grantToken: string;
  grantExpiresAt: string;
  deviceId: string;
}

export type OfflinePinResult =
  | { status: 'ok'; credential: CachedOperatorCredential }
  | { status: 'invalid_pin'; credential: CachedOperatorCredential }
  | { status: 'locked'; credential: CachedOperatorCredential; retryAfterSeconds: number }
  | { status: 'unavailable' | 'expired' };

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

function constantTimeEqual(left: string, right: string): boolean {
  if (left.length !== right.length) return false;
  let diff = 0;
  for (let i = 0; i < left.length; i += 1) {
    diff |= left.charCodeAt(i) ^ right.charCodeAt(i);
  }
  return diff === 0;
}

export async function deriveLocalPinVerifier(pin: string, saltHex: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      salt: hexToBytes(saltHex).buffer as ArrayBuffer,
      iterations: LOCAL_PIN_ITERATIONS,
    },
    key,
    256,
  );
  return bytesToHex(new Uint8Array(bits));
}

async function deriveGrantEncryptionKey(pin: string, saltHex: string): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      salt: hexToBytes(saltHex).buffer as ArrayBuffer,
      iterations: LOCAL_PIN_ITERATIONS,
    },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function encryptGrantTokenForLocalCache(
  grantToken: string,
  pin: string,
): Promise<{
  grant_token_ciphertext: string;
  grant_token_salt: string;
  grant_token_iv: string;
}> {
  const salt = new Uint8Array(16);
  const iv = new Uint8Array(12);
  crypto.getRandomValues(salt);
  crypto.getRandomValues(iv);
  const saltHex = bytesToHex(salt);
  const key = await deriveGrantEncryptionKey(pin, saltHex);
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(grantToken),
  );
  return {
    grant_token_ciphertext: bytesToHex(new Uint8Array(ciphertext)),
    grant_token_salt: saltHex,
    grant_token_iv: bytesToHex(iv),
  };
}

export async function decryptGrantTokenFromLocalCache(
  credential: CachedOperatorCredential,
  pin: string,
): Promise<string> {
  const key = await deriveGrantEncryptionKey(pin, credential.grant_token_salt);
  const iv = hexToBytes(credential.grant_token_iv);
  const ciphertext = hexToBytes(credential.grant_token_ciphertext);
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv.buffer as ArrayBuffer },
    key,
    ciphertext.buffer as ArrayBuffer,
  );
  const token = new TextDecoder().decode(plaintext);
  if (!/^[0-9a-f]{64}$/.test(token)) throw new Error('cached_grant_token_invalid');
  return token;
}

function cacheKey(scope: OperatorCacheScope): string {
  return `shared_tablet_operators_v1:${scope.tenantId}:${scope.tabletUserId}:${scope.deviceId}`;
}

function emptyCache(): OperatorCredentialCache {
  return {
    version: CACHE_VERSION,
    operators: [],
    credentials: {},
    updated_at: new Date().toISOString(),
  };
}

async function loadCache(scope: OperatorCacheScope): Promise<OperatorCredentialCache> {
  const raw = await getMeta(cacheKey(scope));
  if (!raw) return emptyCache();
  try {
    const parsed = OperatorCredentialCacheSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : emptyCache();
  } catch {
    return emptyCache();
  }
}

async function saveCache(
  scope: OperatorCacheScope,
  cache: OperatorCredentialCache,
): Promise<void> {
  await setMeta(cacheKey(scope), JSON.stringify({
    ...cache,
    updated_at: new Date().toISOString(),
  }));
  await flushLocalDb();
}

export async function readCachedOperatorRoster(
  scope: OperatorCacheScope,
): Promise<SharedTabletOperator[]> {
  return (await loadCache(scope)).operators;
}

export async function readCachedOperatorPinRequired(scope: OperatorCacheScope): Promise<boolean> {
  return (await getMeta(`${cacheKey(scope)}:pin_required`)) !== 'false';
}

/** Call only with the boolean returned by shared_tablet_pin_required_v1. */
export async function cacheVerifiedOperatorPinPolicy(scope: OperatorCacheScope, pinRequired: boolean): Promise<void> {
  await setMeta(`${cacheKey(scope)}:pin_required`, JSON.stringify(pinRequired));
  await flushLocalDb();
}

export async function reconcileCachedOperatorRoster(
  scope: OperatorCacheScope,
  operators: SharedTabletOperator[],
  pinRequired = true,
): Promise<void> {
  const cache = await loadCache(scope);
  const current = new Map(operators.map((operator) => [operator.tenant_personnel_id, operator]));
  const credentials = Object.fromEntries(
    Object.entries(cache.credentials).filter(([personnelId, credential]) => {
      const operator = current.get(personnelId);
      return !!operator && operator.identity_fingerprint === credential.identity_fingerprint
        && (credential.pin_required
          ? operator.pin_enabled && operator.pin_version === credential.pin_version
          : !pinRequired);
    }),
  );
  await saveCache(scope, { ...cache, operators, credentials });
}

export async function cacheOnlineOperatorGrant(input: {
  scope: OperatorCacheScope;
  pin: string;
  pinRequired?: boolean;
  grant: SharedTabletUnlockSuccess;
}): Promise<ActiveSharedTabletOperator> {
  const pinRequired = input.pinRequired ?? true;
  if (!pinRequired && await readCachedOperatorPinRequired(input.scope)) {
    throw new Error('shared_tablet_no_pin_policy_unverified');
  }
  const cache = await loadCache(input.scope);
  const salt = new Uint8Array(16);
  crypto.getRandomValues(salt);
  const verifierSalt = bytesToHex(salt);
  const encryptedGrant = await encryptGrantTokenForLocalCache(
    input.grant.grant_token,
    input.pin,
  );
  const credential: CachedOperatorCredential = {
    tenant_personnel_id: input.grant.tenant_personnel_id,
    full_name: input.grant.full_name,
    role: input.grant.role,
    app_role: input.grant.app_role,
    account_user_id: input.grant.account_user_id,
    identity_fingerprint: input.grant.identity_fingerprint,
    pin_required: pinRequired,
    pin_version: input.grant.pin_version,
    grant_id: input.grant.grant_id,
    ...encryptedGrant,
    grant_expires_at: input.grant.grant_expires_at,
    offline_expires_at: new Date(Math.min(
      Date.parse(input.grant.grant_expires_at),
      Date.now() + OFFLINE_GRANT_MAX_AGE_MS,
    )).toISOString(),
    verifier_salt: verifierSalt,
    verifier_hash: await deriveLocalPinVerifier(input.pin, verifierSalt),
    failed_attempts: 0,
    locked_until: null,
  };
  await saveCache(input.scope, {
    ...cache,
    // The first successful unlock can create the signing identity server-side.
    operators: cache.operators.map((operator) => operator.tenant_personnel_id === credential.tenant_personnel_id
      ? { ...operator, identity_fingerprint: credential.identity_fingerprint,
          ...(pinRequired ? { pin_enabled: true, pin_version: credential.pin_version } : {}) }
      : operator),
    credentials: {
      ...cache.credentials,
      [credential.tenant_personnel_id]: credential,
    },
  });
  return activeOperatorFromCredential(
    credential,
    input.scope.deviceId,
    input.grant.grant_token,
  );
}

export async function verifyOfflinePinValue(
  credential: CachedOperatorCredential,
  pin: string,
  now = Date.now(),
): Promise<OfflinePinResult> {
  if (
    Date.parse(credential.grant_expires_at) <= now
    || Date.parse(credential.offline_expires_at) <= now
  ) return { status: 'expired' };
  const lockedUntil = credential.locked_until ? Date.parse(credential.locked_until) : 0;
  if (lockedUntil > now) {
    return {
      status: 'locked',
      credential,
      retryAfterSeconds: Math.max(1, Math.ceil((lockedUntil - now) / 1_000)),
    };
  }

  const verifier = await deriveLocalPinVerifier(pin, credential.verifier_salt);
  if (!constantTimeEqual(verifier, credential.verifier_hash)) {
    const attempts = credential.failed_attempts + 1;
    const next = {
      ...credential,
      failed_attempts: attempts,
      locked_until: attempts >= 5 ? new Date(now + 5 * 60_000).toISOString() : null,
    };
    if (next.locked_until) {
      return { status: 'locked', credential: next, retryAfterSeconds: 300 };
    }
    return { status: 'invalid_pin', credential: next };
  }

  return {
    status: 'ok',
    credential: { ...credential, failed_attempts: 0, locked_until: null },
  };
}

export async function unlockCachedOperator(input: {
  scope: OperatorCacheScope;
  tenantPersonnelId: string;
  pin: string;
}): Promise<OfflinePinResult & { activeOperator?: ActiveSharedTabletOperator }> {
  const cache = await loadCache(input.scope);
  const credential = cache.credentials[input.tenantPersonnelId];
  if (!credential) return { status: 'unavailable' };
  const pinRequired = await readCachedOperatorPinRequired(input.scope);
  const operator = cache.operators.find((entry) => entry.tenant_personnel_id === input.tenantPersonnelId);
  if (credential.pin_required !== pinRequired || !operator
    || operator.identity_fingerprint !== credential.identity_fingerprint
    || (credential.pin_required && (!operator.pin_enabled || operator.pin_version !== credential.pin_version))) {
    return { status: 'unavailable' };
  }
  const result = await verifyOfflinePinValue(credential, input.pin);
  if ('credential' in result) {
    await saveCache(input.scope, {
      ...cache,
      credentials: {
        ...cache.credentials,
        [input.tenantPersonnelId]: result.credential,
      },
    });
  }
  return result.status === 'ok'
    ? {
        ...result,
        activeOperator: activeOperatorFromCredential(
          result.credential,
          input.scope.deviceId,
          await decryptGrantTokenFromLocalCache(result.credential, input.pin),
        ),
      }
    : result;
}

function activeOperatorFromCredential(
  credential: CachedOperatorCredential,
  deviceId: string,
  grantToken: string,
): ActiveSharedTabletOperator {
  return {
    tenantPersonnelId: credential.tenant_personnel_id,
    fullName: credential.full_name,
    role: credential.role,
    appRole: credential.app_role,
    accountUserId: credential.account_user_id,
    identityFingerprint: credential.identity_fingerprint,
    pinVersion: credential.pin_version,
    grantId: credential.grant_id,
    grantToken,
    grantExpiresAt: credential.grant_expires_at,
    deviceId,
  };
}
